import React, { ReactElement, useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { map, filter } from 'lodash';
import accounting from 'accounting';
import TranslateManager from 'ts-translate-js';
import { ButtonsList } from '../../molecules';
import { MembershipCardPubs, TicketSelectionWarningList } from '../../molecules/buytickets';
import { MembershipCardStatusEnum, PleaseWaitSpinner } from '../../../../jsxctl/utilctl';
//  { useTicketPrices } from '../../../mvcapi/hooks';
import { MembershipCardConditionPopup } from '../memberships';
import type { PriceChangeSuggestion, UserMembershipCard } from '../../../mvcapi/models/buyticket/user-membership-card';
import type { ButtonInfo } from '../../molecules';
import type { PriceGroupModel } from '../../../mvcapi/models/buyticket';

import './ticket-price-single-selection.scss';
import { PriceListQuantitySelector } from '../../molecules/buytickets/price-list-quantity-selector';
import { getWarningsListWithSpecificFilters, isPriceGroupCreditCardConditionSeen } from '../../../logic';
import { useMVCPriceGroupModelsCache } from '../../../logic/hooks/caching/use-mvc-price-group-model-cache';
import { PromoProposalControl } from '../promotions/promo-proposal-control';
import { useIFrameResize } from '../../../logic/hooks/utils';
import { useCartControl } from '../../../logic/hooks/use-cart-control';
import { SiteCustomizationContext } from '../../../logic/reactcontext/site-customization-context';
import { SiteSettings } from '@tsinternetsale/common';
import { useAddTicketsToCartStore } from '../../../stores';
import { PriceQuantity } from '../../../mvcapi/models/buyticket/ticketselection';
import { useAddPackageToCartStoreExt } from '../../../stores/add-to-cart-stores/add-package-to-cart-store-ext';
import { AddToCartPackageItem, AddToCartTicketItem } from '../../../stores/add-to-cart-stores/models/add-to-cart-items';
import { useAddToCartRequest } from '../../../logic/hooks/ticketselection/use-add-to-cart-request';
import { AutoFindTicketModel } from '../../../logic/models';
const tr = TranslateManager.boundTR;

export type TicketPriceSingleSelectionProps = {
    //promoInfo: PromoInfoShort;
    //selectedTickets: SelectedTicketInfo[],
    //onPromoUpdated: () => void;
}

// INFO: New sale

export function TicketPriceSingleSelection(_props: TicketPriceSingleSelectionProps): ReactElement {
    const promoInfo = useAddTicketsToCartStore(s => s.eventDate?.PromoInfo);
    const tempSelectedAddToCartItems = useAddTicketsToCartStore(s => s.tempSelectedAddToCartItems);
    const cancelPriceSelectionProcess = useAddTicketsToCartStore(s => s.cancelPriceSelectionProcess);
    const creditCardConditionsSeen = useAddTicketsToCartStore(s => s.creditCardConditionsSeen);
    const onPromoUpdated = useAddTicketsToCartStore(s => s.onPromoUpdatedCallback);
    const onFindTicketsWhenNoPrice = useAddTicketsToCartStore(s => s.onFindTicketsWhenNoPriceCallBack);
    const applyPriceChangeSuggestions = useAddTicketsToCartStore(s => s.applyPriceChangeSuggestions);
    // const [ eventDateId, selectedTicketsByEventDateId ] = useAddTicketsToCartStore(s => [ s.eventDateId, s.selectedTicketsByEventDateId ]);
    const setLastSelectedPriceGroup = useAddTicketsToCartStore(s => s.setLastSelectedPriceGroup);
    
    const [activeMembershipCards, setActiveMembershipCards] = useState<UserMembershipCard[]>([]);
    const [membershipCardStatus, setMembershipCardStatus] = useState<number>(-1);
    //const [refreshPriceList, setRefreshPriceList] = useState<boolean>(false);

    const [showCreditCardConditionModal, setShowCreditCardConditionModal] = useState<boolean>(false);
    const [priceCreditCardCondition, setPriceCreditCardCondition] = useState<PriceGroupModel>(null);
    const [creditCardConditionCb, setCreditCardConditionCb] = useState<{ cb: (accept: boolean) => void }>(null);

    const [ isAPackageSale ] = useAddPackageToCartStoreExt(s => [ s.isAPackageSale ]);
    const selectedSection = useAddTicketsToCartStore(s => s.selectedSection);
    const ticketCount = useAddTicketsToCartStore(s => s.ticketCount);
    
    const [packagePanelData, packageLoading] = useAddPackageToCartStoreExt(s => [s.pricePanel, s.fetchingPricePanel]);
    const [ticketPanelData, ticketLoading] = useAddTicketsToCartStore(s => [s.pricePanel, s.fetchingPricePanel]);
    const refreshPackagePricesListPanel = useAddPackageToCartStoreExt(s => s.refreshPricesListPanel);
    const refreshEventDatePriceListPanel = useAddTicketsToCartStore(s => s.refreshPricesListPanel);
    const confirmPriceSelectionCompleted = useAddTicketsToCartStore(s => s.confirmPriceSelectionCompleted);
    const confirmPriceSelectionCompletedPackage = useAddPackageToCartStoreExt(s => s.confirmPriceSelectionCompleted);


    const panelData = isAPackageSale() ? packagePanelData : ticketPanelData;
    const loading = isAPackageSale() ? packageLoading : ticketLoading;
    
    const [, putPricesInfoInCache] = useMVCPriceGroupModelsCache([]);
    const hiddenPromo = useAddTicketsToCartStore(s => s.hiddenPromo); // this could go in the deeper component once the store is implemented in the old package sale also

    const [onContentChange] = useIFrameResize();
    const cartControl = useCartControl();

    const modalContainer: React.RefObject<HTMLDivElement> = useRef();

    const { siteSettings } = useContext(SiteCustomizationContext);

    const refreshPricesListPanel = useCallback(() => {
        if(isAPackageSale())
            refreshPackagePricesListPanel(tempSelectedAddToCartItems as AddToCartPackageItem[]);
        else
            refreshEventDatePriceListPanel(tempSelectedAddToCartItems);
    }, [isAPackageSale, refreshPackagePricesListPanel, tempSelectedAddToCartItems, refreshEventDatePriceListPanel]);

    useEffect(() => { 
        setActiveMembershipCards(panelData?.activeMembershipCards ?? []);
        // setRefreshPriceList(false);

    }, [panelData, loading]);

    useEffect(() => {
        if (!loading && panelData?.priceGroups)
            putPricesInfoInCache(panelData.priceGroups); // Update the cache with new received price groups. This cache is used by the event tracker.
    }, [panelData, loading, putPricesInfoInCache]);

    const hasSpecialMessage = useMemo(() => {
        return panelData?.sectionSpecialMessage !== "";
    }, [panelData]);

    const handleMembershipTypeAddedToCart = async (membershipTypeId: string, status: number, priceChangeSuggestions: PriceChangeSuggestion[]) => {
        if(priceChangeSuggestions && priceChangeSuggestions.length > 0)
            applyPriceChangeSuggestions(priceChangeSuggestions);

        if (cartControl)
            cartControl.refreshCart();

        //setRefreshPriceList(true);
        refreshPricesListPanel();
        setMembershipCardStatus(status);
    };

    const handleOnPromoUpdated = () => {
        onPromoUpdated();
        //setRefreshPriceList(true);
        refreshPricesListPanel();
    };

    const membershipCardMessage: ReactElement = useMemo(() => {
        if (membershipCardStatus === -1 || activeMembershipCards.length === 0) return <></>;

        const messageMembershipCardStatus = (membershipCardStatus == MembershipCardStatusEnum.NewCard
            ? tr("ticketpriceselection.membershipcardaddingsuccessfully")
            : tr("ticketpriceselection.membershipcardhasbeenfoundsuccessfully"));

        return <div className="alert alert-info">{messageMembershipCardStatus}</div>;
    }, [membershipCardStatus, activeMembershipCards.length]);

    const handleShowCrediCardConditionModal = (priceGroup: PriceGroupModel, cb: (accept: boolean) => void) => {
        setPriceCreditCardCondition(priceGroup);
        setShowCreditCardConditionModal(true);
        setCreditCardConditionCb({ cb: cb });
    };

    const handleSelectionCompletion = useCallback((pricesQty: PriceQuantity[]) => {
        if(isAPackageSale())
            confirmPriceSelectionCompletedPackage(tempSelectedAddToCartItems, pricesQty);
        else
            confirmPriceSelectionCompleted(tempSelectedAddToCartItems, pricesQty);

    }, [isAPackageSale, confirmPriceSelectionCompletedPackage, tempSelectedAddToCartItems, confirmPriceSelectionCompleted]);

    const handleCreditCardConditionForPrice = useCallback(async (priceGroup: PriceGroupModel): Promise<boolean> => {
        if (panelData.activeCreditCardConditions.length === 0 || !creditCardConditionsSeen[priceGroup.RequiredCreditCardConditionID]) {
            const accept = await new Promise<boolean>((resolve) => {
                handleShowCrediCardConditionModal(priceGroup, resolve);
            });

            setShowCreditCardConditionModal(false);
            return accept;
        }
        return true;
    }, [panelData, creditCardConditionsSeen]);

    const handlePriceGroupSelection = useCallback(async (pg: PriceGroupModel, requiredCreditCardCondition: boolean = true) => {
        let canSelectPriceGroup = true;

        if (pg.RequiresCreditCardCondition) {
            canSelectPriceGroup = !requiredCreditCardCondition || isPriceGroupCreditCardConditionSeen(pg.RequiredCreditCardConditionID, panelData.activeCreditCardConditions, creditCardConditionsSeen);
            if (!canSelectPriceGroup) {
                canSelectPriceGroup = await handleCreditCardConditionForPrice(pg);
            }
        }

        if (canSelectPriceGroup) {
            const priceQty: PriceQuantity = {
                priceGroup: pg,
                quantity: 1
            };
            
            setLastSelectedPriceGroup(pg);

            handleSelectionCompletion([priceQty]);
        }
    }, [panelData.activeCreditCardConditions, creditCardConditionsSeen, handleCreditCardConditionForPrice, setLastSelectedPriceGroup, handleSelectionCompletion]);


    const fakeAddToCartRequest = useAddToCartRequest();

    const getAutoFindTicket = useCallback((): AutoFindTicketModel => {
        let autoFindTicket: AutoFindTicketModel = null;
        if(selectedSection)
            autoFindTicket = { sectionid: selectedSection.ID, qty: ticketCount };
        /* TODO: Ce code est a garder lorsqu'on fera la rechercher automatique pour le click sur siege et rechercher rapide
        if (!isAPackageSale() && tempSelectedAddToCartItems && tempSelectedAddToCartItems.length > 0) {
            const firstItem = tempSelectedAddToCartItems[0] as AddToCartTicketItem;
            autoFindTicket = { sectionid: firstItem.ticket.ticket.SectionID, qty: ticketCount };
        }
        */
        return autoFindTicket;
    }, [selectedSection, ticketCount]);

    const getModalContent = (siteSettings: SiteSettings) => {
        if (loading) {
            return <div className="loading-panel">
                <PleaseWaitSpinner size="md" />
            </div>;
        }

        if (panelData == null || panelData.priceGroups.length === 0) {

            let autoFindTicket: AutoFindTicketModel = getAutoFindTicket();

            let autoFindTicketButton = null;
            // On ne gère pas toute suite le multi event car trop compliqué de rechercher des nouveaux sièges dans les forfaits (modif trop longue)
            if(!isAPackageSale() && autoFindTicket)
                autoFindTicketButton = <button className="btn btn-primary" onClick={() => onFindTicketsWhenNoPrice(autoFindTicket)}>{tr("Chercher dans une autre section")}</button>;


            return <>
                <h5 className="text-uppercase">{tr("Choix du tarif")}</h5>
                <div className="no-price-group">
                    <strong>{tr("Il n'y a aucun tarif disponible dans cette section pour le moment. Veuillez sélectionner un autre siège.")}</strong>
                    <div className="actions">
                        {autoFindTicketButton}
                        <button className="btn btn-default" onClick={cancelPriceSelectionProcess}>{tr("Annuler")}</button>
                    </div>
                </div>
            </>;
        }

        let priceGroupsSection: ReactElement = null;

        if (ticketCount > 1) {
            priceGroupsSection = <PriceListQuantitySelector
                quantityToSelect={ticketCount}
                priceGroups={panelData.priceGroups}
                ticketSelectionWarningReport={panelData.ticketSelectionWarningReport}
                onCancel={cancelPriceSelectionProcess}
                onSelection={handleSelectionCompletion}
                onRequiredCreditCardCondition={handleCreditCardConditionForPrice}
                activeCreditCardConditions={panelData.activeCreditCardConditions}
                sectionSpecialMessage={panelData.sectionSpecialMessage}
                taxesOnlyAtCheckout={siteSettings.taxesOnlyAtCheckout}
            />;
        } else {
            const buttons: ButtonInfo[] = map(filter(panelData.priceGroups, ["Hidden", false]), (pg: PriceGroupModel) => {
                const curPriceWarningList = panelData.ticketSelectionWarningReport.priceListWarnings.find(wl => wl.priceId === pg.PriceID);
                const warnings = curPriceWarningList?.warnings ?? [];
                const filteredWarnings = getWarningsListWithSpecificFilters(warnings);
                const priceDisabledByWarnings = filteredWarnings.find(w => w.disabled) != undefined;

                return {
                    class: 'btn-primary btn-price-group',
                    disabled: pg.Disabled || priceDisabledByWarnings,
                    children: [
                        <div key={pg.ID} className="price-group">
                            <div className="info">
                                <strong className="name">{pg.Name}</strong>
                                <div className="price accented-text"><strong>{accounting.formatMoney(siteSettings.taxesOnlyAtCheckout ? pg.TotalBeforeTaxes : pg.Total)}</strong></div>
                            </div>
                            <TicketSelectionWarningList warnings={filteredWarnings} />
                        </div>
                    ],
                    onClick: () => handlePriceGroupSelection(pg)
                };
            });

            priceGroupsSection = <>
                <ButtonsList buttonInfos={buttons} />
                {
                    hasSpecialMessage && <div className="specialMessage">
                        <div className="alert alert-warning" role="alert">
                            <strong>{tr(panelData.sectionSpecialMessage)}</strong>
                        </div>
                    </div>
                }
            </>;
        }


        return <>
            {
                panelData.membershipTypeSavingAmounts && panelData.membershipTypeSavingAmounts.length > 0 && <>
                    <h5 className="text-uppercase">{tr("Êtes-vous membre ?")}</h5>
                    <div className="membership-card-pub">
                        <MembershipCardPubs
                            membershipTypeSaleInfos={panelData.membershipTypeSavingAmounts}
                            activeMembershipCards={activeMembershipCards}
                            refreshActiveMembershipCards={handleMembershipTypeAddedToCart}
                        />
                        {membershipCardMessage}
                    </div>
                    <br />
                </>
            }
            {
                promoInfo && <>
                    <PromoProposalControl
                        promoInfo={promoInfo}
                        hiddenPromo={hiddenPromo}
                        showProposalMessage={false}
                        onPromoUpdated={handleOnPromoUpdated}
                        onContentChange={onContentChange}
                        onPriceChangeSuggestions={applyPriceChangeSuggestions}
                        addToCartRequest={fakeAddToCartRequest}
                        isAPackageSale={isAPackageSale()}
                    />
                    <br />
                </>
            }
            <h5 className="text-uppercase">{tr("Choisissez votre tarif")}</h5>
            <div className="price-group-list">
                {priceGroupsSection}
            </div>
            <div ref={modalContainer} />
            {
                showCreditCardConditionModal && <MembershipCardConditionPopup
                    container={modalContainer.current}
                    creditCardCondition={
                        {
                            id: priceCreditCardCondition.RequiredCreditCardConditionID,
                            name: {
                                fr: priceCreditCardCondition.CreditCardConditionName,
                                en: priceCreditCardCondition.CreditCardConditionName
                            },
                            explanationText: {
                                fr: priceCreditCardCondition.CreditCardConditionInfoText,
                                en: priceCreditCardCondition.CreditCardConditionInfoText
                            }
                        }}
                    onConfirmPaidWithCreditCardCondition={() => creditCardConditionCb.cb(true)}
                    onPopupClose={() => creditCardConditionCb.cb(false)}
                />
            }
            {
                ticketCount === 1 && <div className="actions">
                    <button onClick={cancelPriceSelectionProcess} className="btn btn-default">
                        {tr("Annuler")}
                    </button>
                </div>
            }
        </>;
    };

    return getModalContent(siteSettings);
}
